import { QuizWithProctoringOptions } from "./assessments-page-helper";
import {
	EditQuizWrapper,
	EditQuizWrapperRef,
	ExemptedStudent,
	api,
	VerityApi,
	useMobileLayout,
} from "@verity/components";
import { useContext, useEffect, useRef, useState } from "react";
import { CoreModal, CoreSpinner, coreToast } from "@verity/components/core";
import { AssessmentInfo } from "@verity/api-client/types";
import i18n from "../../../i18n";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { Prisma } from "@verity/database/prisma";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import { PortalControllerContext } from "../../context";
import { Id } from "react-toastify";

interface EditAssessmentOptionsModalProps {
	onClose: () => void;
	assessment: { quizId: string };
	isFirstTimeThirdPartyCreation?: boolean;
	afterSave?: (options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0]) => void;
}

export default function EditAssessmentOptionsModal(props: EditAssessmentOptionsModalProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	// const [saveOption, setSaveOption] = useState<number>(0); // it is used to notify
	const [quizInfo, setQuizInfo] = useState<AssessmentInfo | null>(null);
	const [enforcedProfilesOnly, setEnforcedProfilesOnly] = useState<boolean>(true);
	const quiz = props.assessment;
	const isMobileLayout = useMobileLayout();

	const childRef = useRef<EditQuizWrapperRef>(null);
	useEffect(() => {
		void (async () => {
			if (!quiz) {
				return;
			}
			setEnforcedProfilesOnly(await api.areProctoringProfilesEnforced());
			if (!lmsProvidedCourseId) {
				throw new Error(`context missing: lmsProvidedCourseId=${lmsProvidedCourseId}`);
			}
			setQuizInfo({
				lms,
				hostname: instituteIntegrationHostname,
				courseId: lmsProvidedCourseId,
				quizId: quiz.quizId,
			});
		})();
	}, []);

	const [backEnabled, setBackEnabled] = useState(true);
	const [nextEnabled, setNextEnabled] = useState(true);
	const [isFinalPage, setIsFinalPage] = useState(false);
	const [saveEnabled, setSaveEnabled] = useState(true);
	const [closeEnabled, setCloseEnabled] = useState(true);

	//the exemptedstudentInfo that is used to upload
	const [uploadedExemptedStudentsMap, setUploadedExemptedStudentsMap] = useState<Map<number, ExemptedStudent>>(
		new Map(),
	);

	const [uploadedProfilesMap, setUploadedProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(new Map());

	function hasQuizInfo(quiz: unknown): quiz is QuizWithProctoringOptions {
		return (quiz as QuizWithProctoringOptions).quizInfo !== undefined;
	}

	const quizName = hasQuizInfo(quiz)
		? `${((quiz.quizInfo as Prisma.JsonObject) ?? {})["name"]?.toString() ?? "?"}`
		: undefined;

	return (
		<div tabIndex={2}>
			<CoreModal
				title={isMobileLayout ? undefined : i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.title")}
				open
				onClose={() => {
					childRef?.current?.closeButtonHandler();
				}}
				padding={isMobileLayout ? undefined : "12px 36px 0px 36px"}
				disableTopCloseButton={!closeEnabled}
				fullScreen={isMobileLayout}
				showTopCloseButton={!isMobileLayout}
				dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
				buttons={[
					{
						text: "Cancel",
						color: "secondary",
						disabled: !closeEnabled,
						onClick: () => childRef?.current?.closeButtonHandler(),
						sx: { marginRight: "auto" },
					},
					{
						text: "Back",
						color: "primary",
						onClick: () => childRef?.current?.backButtonHandler(),
						disabled: !backEnabled,
					},
					{
						text: i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.saveAsProfile"),
						color: "primary",
						disabled: !closeEnabled,
						hide: enforcedProfilesOnly || !isFinalPage,
						style: {
							paddingRight: isMobileLayout ? "5.5px" : undefined,
							paddingLeft: isMobileLayout ? "5.5px" : undefined,
						},
						onClick: () => {
							childRef.current?.saveAsProfile();
						},
					},
					isFinalPage
						? {
								text: "Confirm",
								color: "primary",
								disabled: !saveEnabled || !closeEnabled,
								onClick: () => {
									childRef.current?.saveButtonHandler().catch(console.error);
								},
							}
						: {
								text: "Next",
								color: "primary",
								disabled: !nextEnabled,
								onClick: () => childRef.current?.nextButtonHandler(),
							},
				]}
				minWidth={isMobileLayout ? undefined : "min(90%, 960px)"}
				minHeight={isMobileLayout ? undefined : "min(98%, 650px)"}
				widthFixed
				heightFixed
			>
				<Content
					quizName={quizName}
					quizInfo={quizInfo}
					onClose={props.onClose}
					afterSave={props.afterSave}
					childRef={childRef}
					setBackEnabled={setBackEnabled}
					setNextEnabled={setNextEnabled}
					setSaveEnabled={setSaveEnabled}
					setIsFinalPage={setIsFinalPage}
					setCloseEnabled={setCloseEnabled}
					uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
					setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
					uploadedProfilesMap={uploadedProfilesMap}
					setUploadedProfilesMap={setUploadedProfilesMap}
					isFirsTimeThirdPartyCreation={props.isFirstTimeThirdPartyCreation ?? false}
				/>
			</CoreModal>
		</div>
	);
}

function Content(props: {
	quizName?: string;
	quizInfo: AssessmentInfo | null;
	onClose: () => void;
	afterSave?: (options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0]) => void;
	childRef: React.RefObject<EditQuizWrapperRef>;
	setBackEnabled: (enabled: boolean) => void;
	setNextEnabled: (enabled: boolean) => void;
	setSaveEnabled: (enabled: boolean) => void;
	setIsFinalPage: (value: boolean) => void;
	setCloseEnabled: (enabled: boolean) => void;
	uploadedExemptedStudentsMap: Map<number, ExemptedStudent>;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	uploadedProfilesMap: Map<number, ProctoringOptionProfileV2>;
	setUploadedProfilesMap: React.Dispatch<React.SetStateAction<Map<number, ProctoringOptionProfileV2>>>;
	isFirsTimeThirdPartyCreation: boolean;
}) {
	const loadingToastId = useRef<Id>();

	return props.quizInfo ? (
		<EditQuizWrapper
			quizName={props.quizName}
			quizInfo={props.quizInfo}
			onStartSaveOptions={() => {
				props.setCloseEnabled(false);
				loadingToastId.current = coreToast.loading("Applying your proctor settings...", {
					containerId: PORTAL_TOAST_CONTAINER_ID,
				});
			}}
			onSaveOptions={(success, messages, options) => {
				props.setCloseEnabled(true);
				if (success) {
					props.afterSave?.(options);
					props.onClose();
					coreToast.dismiss();
					coreToast.success(i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyMessage"), {
						autoClose: 2500,
					});
					loadingToastId.current = undefined;
				} else {
					coreToast.updateOrShow(
						loadingToastId.current,
						<>
							{i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyFailedMessage")}
							{messages ? ": " : "."}
							{messages?.map((message) => (
								<div key={message}>{message}</div>
							))}
						</>,
						"error",
						{ containerId: PORTAL_TOAST_CONTAINER_ID, autoClose: 2500 },
					);
					loadingToastId.current = undefined;
				}
			}}
			ref={props.childRef}
			onBackEnabledChange={props.setBackEnabled}
			onNextEnabledChange={props.setNextEnabled}
			onSaveEnabledChange={props.setSaveEnabled}
			onClose={props.onClose}
			setIsFinalPage={props.setIsFinalPage}
			forceThirdParty={(props.quizInfo?.quizInfo?.name as string | undefined)?.startsWith("Third Party: ")}
			onEnabledChange={() => {
				/**empty */
			}}
			uploadedExemptedStudentsMap={props.uploadedExemptedStudentsMap}
			setUploadedExemptedStudentsMap={props.setUploadedExemptedStudentsMap}
			uploadedProfilesMap={props.uploadedProfilesMap}
			setUploadedProfilesMap={props.setUploadedProfilesMap}
			isFirstTimeThirdPartyCreation={props.isFirsTimeThirdPartyCreation}
		/>
	) : (
		<div
			style={{
				height: "512px",
				display: "flex",
				placeContent: "center",
				alignItems: "center",
				flexDirection: "column",
			}}
		>
			<CoreSpinner size={100} />
		</div>
	);
}
